import { bundlePlanForm } from "../components/types";

/** A bundle plan row as returned by the listing endpoint. */
export interface BundlePlanRow {
  id: string;
  mno_id?: string;
  ussd_plan_code?: number;
  bundle_id?: string;
  amount_incl_tax?: number;
  amount_excl_tax?: number;
  internal_subscription_fee?: number;
  /** Selected types mapped to the allowance entered for each. */
  plan_types?: Record<string, string>;
  plan_name?: string;
  plan_detail?: string;
  validity_days?: number;
  created_at?: string;
  updated_at?: string;
}

/**
 * The selectable plan types, each with the placeholder shown for its allowance
 * input. Selecting a type reveals the input beneath it.
 */
export const PLAN_TYPE_OPTIONS = ["Voice", "Data", "SMS"];

export const PLAN_TYPE_PLACEHOLDERS: Record<string, string> = {
  Voice: "Ex. 100 minutes",
  Data: "Ex. 5 GB",
  SMS: "Ex. 100 SMS",
};

/** Maximum plan amount permitted for the amounts and the subscription fee. */
export const MAX_PLAN_AMOUNT = 9999;

/** Maximum validity period, entered as whole days. */
export const MAX_VALIDITY_DAYS = 999;

/** Rows per page, matching the page size MainTable and the API both use. */
export const PAGE_SIZE = 10;

/** Character limits enforced on the free text fields. */
export const BUNDLE_ID_MAX_LENGTH = 15;
export const PLAN_NAME_MAX_LENGTH = 30;
export const PLAN_DETAIL_MAX_LENGTH = 150;
export const PLAN_TYPE_VALUE_MAX_LENGTH = 20;
export const VALIDITY_MAX_LENGTH = 3;

export const bundlePlanInitialState: bundlePlanForm = {
  ussd_plan_code: "",
  plan_name: "",
  bundle_id: "",
  validity_days: "",
  amount_excl_tax: "",
  amount_incl_tax: "",
  internal_subscription_fee: "",
  plan_detail: "",
};

/**
 * Builds the input configuration for the first row of the bundle plan form. This
 * row is identical when creating and editing, so both screens line up.
 */
export const getBundlePlanInputs = (values?: { plan_name?: string; bundle_id?: string }) => ({
  "": {
    "Plan Name": {
      required: true,
      disabled: false,
      id: "plan_name",
      type: "input",
      maxLength: PLAN_NAME_MAX_LENGTH,
      tabIndex: 0,
      placeholder: "Ex: Special Data",
      secondaryText: `${values?.plan_name?.length ?? 0}/${PLAN_NAME_MAX_LENGTH}`,
      // Room for the counter, which renders below the field without adding height.
      extraSpaceBelow: true,
    },
    "Bundle ID": {
      required: true,
      disabled: false,
      id: "bundle_id",
      type: "input",
      maxLength: BUNDLE_ID_MAX_LENGTH,
      tabIndex: 0,
      placeholder: "Ex: 12344",
      secondaryText: `${values?.bundle_id?.length ?? 0}/${BUNDLE_ID_MAX_LENGTH}`,
      extraSpaceBelow: true,
    },
    "Validity (Days)": {
      required: true,
      disabled: false,
      id: "validity_days",
      type: "input",
      maxLength: VALIDITY_MAX_LENGTH,
      tabIndex: 0,
      placeholder: "000",
    },
  },
});

/**
 * The amount row, rendered after the plan type selection.
 *
 * When editing, the USSD plan code follows the amounts, always disabled: the code
 * is already in circulation and the edit endpoint rejects it. It is absent when
 * creating, since the backend allocates it on save.
 */
export const getAmountInputs = (edit?: boolean) => ({
  "": {
    "Amount Excl. Tax": {
      required: true,
      disabled: false,
      id: "amount_excl_tax",
      type: "currency",
      tabIndex: 0,
      min: 0,
      max: MAX_PLAN_AMOUNT,
      // The currency field renders its error absolutely, so the row needs room for a
      // wrapped message rather than letting it overlap the fields below.
      extraSpaceBelow: true,
    },
    "Amount Incl. Tax": {
      required: true,
      disabled: false,
      id: "amount_incl_tax",
      type: "currency",
      tabIndex: 0,
      min: 0,
      max: MAX_PLAN_AMOUNT,
      extraSpaceBelow: true,
    },
    "Internal Subscription Fee": {
      required: true,
      disabled: false,
      id: "internal_subscription_fee",
      type: "currency",
      tabIndex: 0,
      min: 0,
      max: MAX_PLAN_AMOUNT,
      extraSpaceBelow: true,
    },
    ...(edit && {
      "USSD Plan Code": {
        required: true,
        disabled: true,
        id: "ussd_plan_code",
        type: "input",
        tabIndex: 0,
      },
    }),
  },
});
